import { Button, DatePicker, Descriptions, Form, Input, Radio, Select, Space, theme, Typography, Row, Col, Card, message } from 'antd';
import {
  CredentialVerificationCaseV1Application,
  CredentialVerificationCaseV1ApplicationReview,
  StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateMutation,
  Entity
} from '../../../../../../generated';
import { useEffect, useReducer, useState } from 'react';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, FormatDate } from '../../../../../../constants';
import dayjs from 'dayjs';
import { Country } from '../../../../../../types/country';
import StateSelect from '../../../../../shared/state-select';
import { AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { CredCaseFileUploadButtonWithPreviewContainer } from './cred-case-file-upload-button-with-preview.container';
import { FetchResult } from '@apollo/client';
import { Helmet } from 'react-helmet';

export interface ApplicationFormValueProps {
  caseName: string;
  credentialType: string;
  isCredentialInEnglish: boolean;
  credentialTitle: string;
  program: string;
  nameOnCredential: string;
  dateCredentialIssuedAt: string;
  issuingInstitution: string | null;
  otherIssuingInstitution: {
    institutionName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zipOrPostalCode: string;
    stateOrProvince: string;
    country: string;
  } | null;
  sendDestination: string;
  isVerifiedWithOther: boolean;
  otherDetails: string;
  verificationResponse: string;
  purgeEligibleAt: Date;
  archiveEligibleAt: Date;
  expirationEligibleAt: Date;
}
interface ApplicationProps {
  caseId: string;
  applicationReview: CredentialVerificationCaseV1ApplicationReview;
  application: CredentialVerificationCaseV1Application;
  caseName: string;
  countryData: Country[];
  entities: Entity[];
  onSave: (
    input: ApplicationFormValueProps
  ) => Promise<FetchResult<StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateMutation>>;
  handleOnUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
  handleOnUploadSuccessEntityProvidedCredentials: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
  caseState: string;
  isCurrentAssigned: boolean;
  purgeEligibleAt: Date;
  archiveEligibleAt: Date;
  expirationEligibleAt: Date;
  staffUserType: string;
  isSavingApplication: boolean;
  isCaseOrApplicant: string;
}

const PRIMARY_CREDENTIAL = `This is the credential issued by your educational institution after you have completed all requirements for and been awarded your degree qualifying you to practice in your profession. If your credential is not uploaded in the appropriate document category, ECFMG may correct the document category.`

const OTHER = `This is any other credential type related to your professional practice that you may need to have verified. Examples include transcripts, specialty qualifications, licenses, or other credentials.`

export const Application: React.FC<ApplicationProps> = (props) => {
  const [isSavingApplication, setIsSavingApplication] = useState(props.isSavingApplication);

  useEffect(() => {
    setIsSavingApplication(props.isSavingApplication);
    dispatch({ type: 'save-date-details' });
  }, [props.isSavingApplication]);

  const labelStyle = {
    fontSize: '12px'
  };

  const labelTitleStyle = {
    fontSize: '15px',
    fontWeight: 400
  };

  const descriptionStyle = {
    paddingLeft: '8px'
  };

  const verificationStatus = [
    {
      key: 'verificationAccepted',
      value: 'verificationAccepted',
      label: 'Verification Accepted'
    },
    {
      key: 'notVerifiedAttemptLimitReached',
      value: 'notVerifiedAttemptLimitReached',
      label: 'Not Verified - Attempt Limit Reached'
    },
    {
      key: 'notVerifiedNegativeResponse',
      value: 'notVerifiedNegativeResponse',
      label: 'Not Verified - Negative Response'
    },
    {
      key: 'notVerifiedUnableToVerify',
      value: 'notVerifiedUnableToVerify',
      label: 'Not Verified - Unable to Verify'
    },
    {
      key: 'notVerifiedOther',
      value: 'notVerifiedOther',
      label: 'Not Verified - Other'
    }
  ];

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();
  const { TextArea } = Input;

  interface ApplicationFormState {
    isCredentialDetailsEditable: boolean;
    isCaseNameEditable: boolean;
    isCredentialTypeEditable: boolean;
    isIssuingInstitutionEditable: boolean;
    isSendDestinationEditable: boolean;
    isAlternateVerifyingEntityEditable: boolean;
    isVerificationStatusEditable: boolean;
    isEntitySuppliedCredentialEditable: boolean;
    isDatesEditable: boolean;
  }
  const initialStage: ApplicationFormState = {
    isCredentialDetailsEditable: false,
    isCaseNameEditable: false,
    isCredentialTypeEditable: false,
    isIssuingInstitutionEditable: false,
    isSendDestinationEditable: false,
    isAlternateVerifyingEntityEditable: false,
    isVerificationStatusEditable: false,
    isEntitySuppliedCredentialEditable: false,
    isDatesEditable: false
  };

  const ApplicationFormAction = {
    EditCaseName: 'edit-case-name',
    SaveCaseName: 'save-case-name',
    CancelEditCaseName: 'cancel-edit-case-name',
    EditCredentialType: 'edit-credential-type',
    SaveCredentialType: 'save-credential-type',
    CancelEditCredentialType: 'cancel-edit-credential-type',
    EditCredentialDetails: 'edit-credential-details',
    SaveCredentialDetails: 'save-credential-details',
    CancelEditCredentialDetails: 'cancel-edit-credential-details',
    EditIssuingInstitution: 'edit-issuing-institution',
    SaveIssuingInstitution: 'save-issuing-institution',
    CancelEditIssuingInstitution: 'cancel-edit-issuing-institution',
    EditSendDestination: 'edit-send-destination',
    SaveSendDestination: 'save-send-destination',
    CancelEditSendDestination: 'cancel-send-destination',
    EditAlternateVerifyingEntity: 'edit-alternate-verifying-entity',
    SaveAlternateVerifyingEntity: 'save-alternate-verifying-entity',
    CancelEditAlternateVerifyingEntity: 'cancel-alternate-verifying-entity',
    EditVerificationStatus: 'edit-verification-status',
    SaveVerificationStatus: 'save-verification-status',
    CancelEditVerificationStatus: 'cancel-verification-status',
    EditEntitySuppliedCredential: 'edit-entity-supplied-credential',
    CancelEditEntitySuppliedCredential: 'cancel-entity-supplied-credential'
  };

  const reducer = (state: ApplicationFormState, action: { type: string }) => {
    switch (action.type) {
      case 'cancel-edit-dates':
        return {
          ...state,
          isDatesEditable: false,
          isApplicationEditable: false
        };
      case 'edit-dates':
        return {
          ...state,
          isDatesEditable: true,
          isApplicationEditable: false
        };
      case ApplicationFormAction.EditCaseName:
        return {
          ...state,
          isCaseNameEditable: true
        };
      case ApplicationFormAction.SaveCaseName:
        return {
          ...state,
          isCaseNameEditable: false
        };
      case ApplicationFormAction.CancelEditCaseName:
        setIsSavingApplication(false);
        return {
          ...state,
          isCaseNameEditable: false
        };
      case ApplicationFormAction.EditCredentialType:
        return {
          ...state,
          isCredentialTypeEditable: true
        };
      case ApplicationFormAction.SaveCredentialType:
        return {
          ...state,
          isCredentialTypeEditable: false
        };
      case ApplicationFormAction.CancelEditCredentialType:
        setIsSavingApplication(false);
        return {
          ...state,
          isCredentialTypeEditable: false
        };
      case ApplicationFormAction.EditCredentialDetails:
        return {
          ...state,
          isCredentialDetailsEditable: true
        };
      case ApplicationFormAction.SaveCredentialDetails:
        return {
          ...state,
          isCredentialDetailsEditable: false
        };
      case ApplicationFormAction.CancelEditCredentialDetails:
        setIsSavingApplication(false);
        return {
          ...state,
          isCredentialDetailsEditable: false
        };
      case ApplicationFormAction.EditIssuingInstitution:
        return {
          ...state,
          isIssuingInstitutionEditable: true
        };
      case ApplicationFormAction.SaveIssuingInstitution:
        return {
          ...state,
          isIssuingInstitutionEditable: false
        };
      case ApplicationFormAction.CancelEditIssuingInstitution:
        setIsSavingApplication(false);
        return {
          ...state,
          isIssuingInstitutionEditable: false
        };
      case ApplicationFormAction.EditSendDestination:
        return {
          ...state,
          isSendDestinationEditable: true
        };
      case ApplicationFormAction.SaveSendDestination:
        return {
          ...state,
          isSendDestinationEditable: false
        };
      case ApplicationFormAction.CancelEditSendDestination:
        setIsSavingApplication(false);
        return {
          ...state,
          isSendDestinationEditable: false
        };
      case ApplicationFormAction.EditAlternateVerifyingEntity:
        return {
          ...state,
          isAlternateVerifyingEntityEditable: true
        };
      case ApplicationFormAction.SaveAlternateVerifyingEntity:
        return {
          ...state,
          isAlternateVerifyingEntityEditable: false
        };
      case ApplicationFormAction.CancelEditAlternateVerifyingEntity:
        setIsSavingApplication(false);
        return {
          ...state,
          isAlternateVerifyingEntityEditable: false
        };
      case ApplicationFormAction.EditVerificationStatus:
        return {
          ...state,
          isVerificationStatusEditable: true
        };
      case ApplicationFormAction.SaveVerificationStatus:
        return {
          ...state,
          isVerificationStatusEditable: false
        };
      case ApplicationFormAction.CancelEditVerificationStatus:
        setIsSavingApplication(false);
        return {
          ...state,
          isVerificationStatusEditable: false
        };
      case ApplicationFormAction.EditEntitySuppliedCredential:
        return {
          ...state,
          isEntitySuppliedCredentialEditable: true
        };
      case ApplicationFormAction.CancelEditEntitySuppliedCredential:
        setIsSavingApplication(false);
        return {
          ...state,
          isEntitySuppliedCredentialEditable: false
        };
      default:
        return state;
    }
  };

  const handleSaveData = async (values: ApplicationFormValueProps, disPatchApplicationFormAction: string) => {
    try {
      if (values.issuingInstitution === 'Other') {
        values.issuingInstitution = null;
      }
      const response = await props.onSave(values);
      if (response.data?.credentialVerificationCaseV1Update?.status.success) {
        message.success('Application data saved successfully');
        dispatch({ type: disPatchApplicationFormAction });
      } else {
        message.error("Error: Application data can't be saved in this case state");
      }
    } catch (error) {
      message.error('Error saving Application data');
    }
  };

  const [caseNameForm] = Form.useForm<ApplicationFormValueProps>();
  const handleCancelEditCaseName = () => {
    dispatch({
      type: 'cancel-edit-case-name'
    });
    caseNameForm.resetFields();
  };

  const handleSaveCaseName = () => {
    caseNameForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveCaseName);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseTypeForm] = Form.useForm<ApplicationFormValueProps>();
  const handleCancelEditCaseType = () => {
    dispatch({
      type: 'cancel-edit-credential-type'
    });
    caseTypeForm.resetFields();
  };

  const handleSaveCaseType = () => {
    caseTypeForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveCredentialType);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseDetailsForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditCaseDetails = () => {
    dispatch({
      type: 'cancel-edit-credential-details'
    });
    caseDetailsForm.resetFields();
  };

  const handleSaveCaseDetails = () => {
    caseDetailsForm
      .validateFields()
      .then(async (values) => {
        values.dateCredentialIssuedAt = FormatDate(values.dateCredentialIssuedAt, APOLLO_DATE_FORMAT);
        await handleSaveData(values, ApplicationFormAction.SaveCredentialDetails);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseIssuingInstitutionForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditIssuingInstitution = () => {
    dispatch({
      type: 'cancel-edit-issuing-institution'
    });
    caseIssuingInstitutionForm.resetFields();
  };

  const handleSaveIssuingInstitution = () => {
    caseIssuingInstitutionForm
      .validateFields()
      .then(async (values) => {
        if (values.issuingInstitution === 'other') {
          values.issuingInstitution = null;
        }
        await handleSaveData(values, ApplicationFormAction.SaveIssuingInstitution);
        if (values.issuingInstitution !== null) {
          caseIssuingInstitutionForm.setFieldsValue({
            otherIssuingInstitution: {
              institutionName: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              zipOrPostalCode: '',
              stateOrProvince: '',
              country: ''
            }
          });
        }
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseSendDestinationForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditSendDestination = () => {
    dispatch({
      type: 'cancel-send-destination'
    });
    caseSendDestinationForm.resetFields();
  };

  const handleSaveSendDestination = () => {
    caseSendDestinationForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveSendDestination);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseAlternateVerifyingEntityForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditAlternateVerifyingEntity = () => {
    dispatch({
      type: 'cancel-alternate-verifying-entity'
    });
    caseAlternateVerifyingEntityForm.resetFields();
  };

  const handleSaveAlternateVerifyingEntity = () => {
    caseAlternateVerifyingEntityForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveAlternateVerifyingEntity);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const [caseVerificationStatusForm] = Form.useForm<ApplicationFormValueProps>();

  const handleCancelEditVerificationStatus = () => {
    dispatch({
      type: 'cancel-verification-status'
    });
    caseVerificationStatusForm.resetFields();
  };

  const handleSaveVerificationStatus = () => {
    caseVerificationStatusForm
      .validateFields()
      .then(async (values) => {
        await handleSaveData(values, ApplicationFormAction.SaveVerificationStatus);
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const disabled =
    !props.isCurrentAssigned || props.caseState === 'draft' || props.caseState === 'decisionRendered' || (props.caseState === 'revisionRequested' && props.isCurrentAssigned);

  const [isOtherIssuingInstitution, setIsOtherIssuingInstitution] = useState<boolean>(!props.application?.issuingInstitution?.issuingInstitution);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(props.application?.issuingInstitution?.issuingInstitution?.address?.country ?? null);
  const [applicationFormState, dispatch] = useReducer(reducer, initialStage);
  const [overrideDetailsForm] = Form.useForm();

  const handleSaveOverrideDetails = async (values: ApplicationFormValueProps) => {
    props.onSave(values).then(() => {
      message.success('Application data saved successfully');
      dispatch({ type: 'cancel-edit-dates' });
    });
  };

  const isCredentialInEnglish = Form.useWatch('isCredentialInEnglish', caseTypeForm);
  const isVerifiedWithOther = Form.useWatch(['isVerifiedWithOther'], caseAlternateVerifyingEntityForm);

  return (
    <>
      <Helmet>
        <title>{props?.isCaseOrApplicant} - Application</title>
      </Helmet>
      <Card hoverable style={{ marginTop: '20px', padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: 30,
              width: '100%'
            }}
          >
            {!applicationFormState.isCaseNameEditable && (
              <Descriptions
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() => {
                      dispatch({
                        type: 'edit-case-name'
                      });
                    }}
                  >
                    Edit
                  </Button>
                }
                contentStyle={{ paddingLeft: 25 }}
                layout="vertical"
                title={<Typography.Title style={{ ...labelTitleStyle }}>Case Name</Typography.Title>}
              >
                <Descriptions.Item style={{ paddingBottom: '8px' }}>{props.caseName || 'N/A'}</Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isCaseNameEditable && (
              <Form
                form={caseNameForm}
                initialValues={{
                  caseName: props.caseName
                }}
                layout="vertical"
                onFinish={handleSaveCaseName}
              >
                <Row>
                  <Col flex="auto">
                    <Form.Item
                      name={'caseName'}
                      label="Case Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter case name.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter case name" style={{ width: 200 }} disabled={disabled} />
                    </Form.Item>
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditCaseName}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
            <Typography.Text style={{ fontSize: '20px', fontWeight: 600, marginTop: '24px' }}>Applicant Provided Details</Typography.Text>
            <Typography.Text style={{ ...labelStyle, marginBottom: '36px' }}>(Visible to Applicant)</Typography.Text>

            {!applicationFormState.isCredentialTypeEditable && (
              <div>
                <Descriptions
                  extra={
                    <Button
                      type="primary"
                      disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                      onClick={() =>
                        dispatch({
                          type: 'edit-credential-type'
                        })
                      }
                    >
                      Edit
                    </Button>
                  }
                  column={1}
                  style={{ marginBottom: '24px' }}
                  title={<Typography.Text style={{ ...labelTitleStyle }}>What type of Credential is it?</Typography.Text>}
                >
                  <Descriptions.Item>
                    <Typography.Text style={{ fontSize: '18px', fontWeight: '700' }}>
                      {props.application?.credentialType === 'primary' ? 'Primary Credential' : 'Other Credential'}
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item>
                    {props.application?.credentialType === 'primary' ? (
                      <Typography.Text style={{ width: '70%', overflow: 'clip' }}> {PRIMARY_CREDENTIAL}</Typography.Text>
                    ) : (
                      <Typography.Text style={{ width: '70%', overflow: 'clip' }}> {OTHER}</Typography.Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
                <Typography.Text
                  style={{
                    fontWeight: '400',
                    fontSize: '15px',
                    padding: '4px, 0px'
                  }}
                >
                  Uploaded Credential
                </Typography.Text>
                <div style={{ ...descriptionStyle, marginBottom: '24px', marginTop: '24px' }}>
                  <CredCaseFileUploadButtonWithPreviewContainer
                    caseId={props.caseId}
                    title={''}
                    buttonText={'Upload Credential'}
                    permittedContentTypes={['application/pdf']}
                    permittedExtensions={['pdf']}
                    isPreviewOnly={true}
                    isShowFileName={false}
                    fieldName={'credential'}
                    fileName={'credential'}
                    onUploadSuccess={(response) => {
                      props.handleOnUploadSuccess(response, 'credential');
                    }}
                    cropAllowed={false}
                  />
                </div>
                <Typography.Text style={{ ...labelTitleStyle }}>Is the Credential you uploaded in English?</Typography.Text>
                <Typography.Text
                  style={{
                    ...descriptionStyle,
                    fontWeight: '400',
                    fontSize: '15px'
                  }}
                >
                  {props.application.isCredentialInEnglish ? (
                    <Typography.Paragraph style={{ ...descriptionStyle, marginTop: '24px' }}>
                      <Typography.Text strong>Yes,</Typography.Text> it is in English in Latin characters
                    </Typography.Paragraph>
                  ) : (
                    <Typography.Paragraph style={{ ...descriptionStyle, marginTop: '24px' }}>
                      <Typography.Text strong>No,</Typography.Text> I will provide a translation
                    </Typography.Paragraph>
                  )}
                </Typography.Text>
                {!props.application.isCredentialInEnglish && (
                  <div style={{ ...descriptionStyle, marginBottom: '40px' }}>
                    <CredCaseFileUploadButtonWithPreviewContainer
                      caseId={props.caseId}
                      title={''}
                      buttonText={'Upload English Translation'}
                      permittedContentTypes={['application/pdf']}
                      permittedExtensions={['pdf']}
                      isPreviewOnly={true}
                      isShowFileName={false}
                      fieldName={'translation'}
                      fileName={'translation'}
                      onUploadSuccess={(response) => {
                        props.handleOnUploadSuccess(response, 'translation');
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {applicationFormState.isCredentialTypeEditable && (
              <Form
                form={caseTypeForm}
                initialValues={{
                  credentialType: props.application?.credentialType,
                  isCredentialInEnglish: props.application.isCredentialInEnglish
                }}
                layout="vertical"
                onFinish={handleSaveCaseType}
              >
                <Row>
                  <Col style={{ width: '78%' }} flex="auto">
                    <Form.Item
                      name={['credentialType']}
                      valuePropName="value"
                      getValueProps={(value) => ({
                        value: value
                      })}
                      rules={[
                        {
                          required: true
                        }
                      ]}
                      label={<Typography.Paragraph style={{ fontSize: '16px', fontWeight: '500' }}>What type of Credential is it?</Typography.Paragraph>}
                    >
                      <Radio.Group defaultValue={'primary'} size="large" style={{ marginLeft: '10px' }}>
                        <Space direction="vertical" style={{ rowGap: '0px' }}>
                          <Radio style={{ fontSize: '20px', fontWeight: 'bold' }} value="primary" disabled={disabled}>
                            Primary Credential
                          </Radio>
                          <Typography.Paragraph style={{ marginLeft: '24px' }}> {PRIMARY_CREDENTIAL}</Typography.Paragraph>
                          <Radio style={{ fontSize: '20px', fontWeight: 'bold' }} value="other" disabled={disabled}>
                            Other
                          </Radio>
                          <Typography.Paragraph style={{ marginLeft: '24px' }}> {OTHER}</Typography.Paragraph>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item name={['uploadCredential']} label={<Typography.Paragraph style={{ ...labelTitleStyle }}>Upload Credential</Typography.Paragraph>}>
                      <CredCaseFileUploadButtonWithPreviewContainer
                        caseId={props.caseId}
                        title={''}
                        buttonText={'Upload Credential'}
                        permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                        permittedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        isPreviewOnly={false}
                        isShowFileName={false}
                        fieldName={'credential'}
                        fileName={'credential'}
                        onUploadSuccess={(response) => {
                          props.handleOnUploadSuccess(response, 'credential');
                        }}
                        cropAllowed={false}
                      />
                    </Form.Item>
                    <Form.Item
                      name={['isCredentialInEnglish']}
                      valuePropName="value"
                      getValueProps={(value) => ({
                        value: value
                      })}
                      label={<Typography.Paragraph style={{ ...labelTitleStyle }}>Is the Credential you uploaded in English?</Typography.Paragraph>}
                      rules={[
                        {
                          required: true,
                          message: 'Please select an option'
                        }
                      ]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <Radio.Group defaultValue={props.application?.isCredentialInEnglish ?? true} size="large" style={{ marginLeft: '10px' }}>
                        <Space direction="vertical" style={{ rowGap: '0px' }}>
                          <Radio value={true} disabled={disabled}>
                            <Typography.Text strong>Yes,</Typography.Text> it is in English in Latin characters
                          </Radio>
                          <Radio value={false} disabled={disabled}>
                            <Typography.Text strong>No,</Typography.Text> I will provide a translation
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                    {!isCredentialInEnglish && (
                      <Form.Item name={['uploadCredentialTranslation']}>
                        <CredCaseFileUploadButtonWithPreviewContainer
                          caseId={props.caseId}
                          title={''}
                          buttonText={'Upload English Translation'}
                          permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                          permittedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                          isPreviewOnly={false}
                          isShowFileName={false}
                          fieldName={'translation'}
                          fileName={'translation'}
                          onUploadSuccess={(response) => {
                            props.handleOnUploadSuccess(response, 'translation');
                          }}
                        />
                      </Form.Item>
                    )}
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditCaseType}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
            {!applicationFormState.isCredentialDetailsEditable && (
              <Descriptions
                column={2}
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() =>
                      dispatch({
                        type: 'edit-credential-details'
                      })
                    }
                  >
                    Edit
                  </Button>
                }
                title={<Typography.Text style={{ ...labelTitleStyle }}>Credential Details</Typography.Text>}
                labelStyle={labelStyle}
              >
                <Descriptions.Item label="Degree Title">{props.application?.credentialTitle ?? 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Program (if applicable)">{props.application?.program ?? 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Name on Credential">{props.application?.nameOnCredential ?? 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Credential Issued Date">
                  {props.application?.dateCredentialIssuedAt ? FormatDate(props.application?.dateCredentialIssuedAt) : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isCredentialDetailsEditable && (
              <Form
                form={caseDetailsForm}
                initialValues={{
                  credentialTitle: props.application?.credentialTitle,
                  program: props.application?.program,
                  nameOnCredential: props.application?.nameOnCredential,
                  dateCredentialIssuedAt: props.application?.dateCredentialIssuedAt
                }}
                layout="vertical"
                onFinish={handleSaveCaseDetails}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right', padding: 0 }}>
                        <Space>
                          <Button onClick={handleCancelEditCaseDetails}>Cancel</Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Credential Details</Typography.Text>}
                    labelStyle={labelStyle}
                  />
                  <Col flex="auto">
                    <Row style={{ gap: 10 }}>
                      <Form.Item
                        label="Degree Title"
                        name={['credentialTitle']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter a degree title'
                          }
                        ]}
                      >
                        <Input style={{ width: 150 }} disabled={disabled} />
                      </Form.Item>
                      <Form.Item label="Program (if applicable)" name={['program']}>
                        <Input style={{ width: 150 }} disabled={disabled} />
                      </Form.Item>
                    </Row>
                    <Row style={{ gap: 10 }}>
                      <Form.Item
                        label="Name on Credential"
                        name={['nameOnCredential']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter a name on credential'
                          }
                        ]}
                      >
                        <Input style={{ width: 150 }} disabled={disabled} />
                      </Form.Item>
                      <Form.Item
                        label="Credential Issued Date"
                        name={['dateCredentialIssuedAt']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter a credential issued date'
                          }
                        ]}
                        valuePropName="value"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value) : ''
                        })}
                      >
                        <DatePicker
                          style={{ width: 150 }}
                          maxDate={dayjs()}
                          value={props.application?.dateCredentialIssuedAt ? FormatDate(props.application?.dateCredentialIssuedAt) : ''}
                          format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                          disabled={disabled}
                        />
                      </Form.Item>
                    </Row>
                  </Col>
                </Row>
              </Form>
            )}
            <br />

            {!applicationFormState.isIssuingInstitutionEditable && (
              <Descriptions
                title="What is the Issuing Institution?"
                style={{ marginTop: '24px' }}
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() => dispatch({ type: 'edit-issuing-institution' })}
                  >
                    Edit
                  </Button>
                }
                column={1}
              >
                {props.application?.issuingInstitution?.issuingInstitution && (
                  <>
                    <Descriptions.Item label="Institution Name" labelStyle={{ ...labelStyle }}>
                      {props.application?.issuingInstitution?.entityName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Address" labelStyle={{ ...labelStyle }}>
                      {props.application?.issuingInstitution?.issuingInstitution?.address?.addressLine1 && (
                        <>
                          {props.application?.issuingInstitution?.issuingInstitution?.address?.addressLine1}
                          <br />
                        </>
                      )}
                      {props.application?.issuingInstitution?.issuingInstitution?.address?.addressLine2 && (
                        <>
                          {props.application?.issuingInstitution?.issuingInstitution?.address?.addressLine2}
                          <br />
                        </>
                      )}
                      {[
                        props.application?.issuingInstitution?.issuingInstitution.address?.city,
                        props.application?.issuingInstitution?.issuingInstitution.address?.stateOrProvince,
                        props.application?.issuingInstitution?.issuingInstitution.address?.zipOrPostalCode,
                        props.application?.issuingInstitution?.issuingInstitution.address?.country
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Descriptions.Item>
                  </>
                )}
                {!props.application?.issuingInstitution?.issuingInstitution && (
                  <>
                    <Descriptions.Item label="Institution Name">
                      {props.application?.otherIssuingInstitution?.institutionName}
                      <br />
                    </Descriptions.Item>
                    <Descriptions.Item label="Address" labelStyle={{ ...labelStyle }}>
                      {props.application?.otherIssuingInstitution?.addressLine1 && (
                        <>
                          {props.application?.otherIssuingInstitution?.addressLine1}
                          <br />
                        </>
                      )}
                      {props.application?.otherIssuingInstitution?.addressLine2 && (
                        <>
                          {props.application?.otherIssuingInstitution?.addressLine2}
                          <br />
                        </>
                      )}
                      {[
                        props.application?.otherIssuingInstitution?.city,
                        props.application?.otherIssuingInstitution?.stateOrProvince,
                        props.application?.otherIssuingInstitution?.zipOrPostalCode,
                        props.application?.otherIssuingInstitution?.country
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            )}

            {applicationFormState.isIssuingInstitutionEditable && (
              <div style={{ marginTop: '24px' }}>
                <Form
                  form={caseIssuingInstitutionForm}
                  initialValues={{
                    issuingInstitution: props.application?.issuingInstitution?.id || (props.application.otherIssuingInstitution && 'Other'),
                    otherIssuingInstitution: {
                      institutionName: props.application.otherIssuingInstitution?.institutionName,
                      addressLine1: props.application.otherIssuingInstitution?.addressLine1,
                      addressLine2: props.application.otherIssuingInstitution?.addressLine2,
                      city: props.application.otherIssuingInstitution?.city,
                      zipOrPostalCode: props.application.otherIssuingInstitution?.zipOrPostalCode,
                      stateOrProvince: props.application.otherIssuingInstitution?.stateOrProvince,
                      country: props.application.otherIssuingInstitution?.country
                    }
                  }}
                  layout="vertical"
                  onFinish={handleSaveIssuingInstitution}
                >
                  <Row>
                    <Descriptions
                      column={2}
                      layout="vertical"
                      extra={
                        <Col flex={'100px'} style={{ textAlign: 'right', padding: 0 }}>
                          <Space>
                            <Button onClick={handleCancelEditIssuingInstitution}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                              Save
                            </Button>
                          </Space>
                        </Col>
                      }
                      title={<Typography.Text style={{ ...labelTitleStyle }}>What is the Issuing Institution?</Typography.Text>}
                      labelStyle={labelStyle}
                    />
                    <Col flex="auto">
                      <Form.Item
                        label="Institution Name"
                        name={['issuingInstitution']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter an institution name'
                          }
                        ]}
                      >
                        <Select
                          onSelect={(value) => {
                            if (value === 'other' || !value) {
                              setIsOtherIssuingInstitution(true);
                            } else {
                              setIsOtherIssuingInstitution(false);
                            }
                          }}
                          style={{ width: 300 }}
                          disabled={disabled}
                        >
                          {props.entities
                            .filter((entities) => entities.isIssuingInstitution)
                            .map((isIssuingInstitution) => (
                              <Select.Option key={isIssuingInstitution.id} value={isIssuingInstitution.id}>
                                {isIssuingInstitution.entityName}
                              </Select.Option>
                            ))}
                          <Select.Option key={'other'} value={'other'}>
                            Other
                          </Select.Option>
                        </Select>
                      </Form.Item>
                      {isOtherIssuingInstitution && (
                        <>
                          <div>
                            <Form.Item
                              label="Other Institution Name"
                              name={['otherIssuingInstitution', 'institutionName']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter other institution name'
                                }
                              ]}
                            >
                              <Input style={{ width: 300 }} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              label="Address Line 1"
                              name={['otherIssuingInstitution', 'addressLine1']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter an address line 1'
                                }
                              ]}
                            >
                              <Input style={{ width: 300 }} disabled={disabled} />
                            </Form.Item>

                            <Form.Item label="Address Line 2" name={['otherIssuingInstitution', 'addressLine2']}>
                              <Input style={{ width: 300 }} disabled={disabled} />
                            </Form.Item>
                          </div>
                          <Form.Item
                            label="Country"
                            name={['otherIssuingInstitution', 'country']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter a country'
                              }
                            ]}
                          >
                            <Select
                              placeholder="Select a country"
                              showSearch
                              onChange={(value) => setSelectedCountry(value)}
                              optionFilterProp="children"
                              style={{ width: 300 }}
                              disabled={disabled}
                            >
                              {props.countryData.map((country) => (
                                <Select.Option key={country.countryCode} value={country.countryCode}>
                                  {country.countryName}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <Form.Item
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter a state/province'
                                }
                              ]}
                            >
                              <StateSelect
                                name={['otherIssuingInstitution', 'stateOrProvince']}
                                selectedCountry={selectedCountry}
                                countryData={props.countryData}
                                disabled={disabled}
                              />
                            </Form.Item>
                            <Form.Item
                              label="City"
                              name={['otherIssuingInstitution', 'city']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter a city'
                                }
                              ]}
                            >
                              <Input style={{ width: 150 }} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              label="Zip/Postal Code"
                              name={['otherIssuingInstitution', 'zipOrPostalCode']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter a zip/postal code'
                                }
                              ]}
                            >
                              <Input style={{ width: 150 }} disabled={disabled} />
                            </Form.Item>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                </Form>
              </div>
            )}
            {!applicationFormState.isSendDestinationEditable && (
              <Descriptions
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    onClick={() =>
                      dispatch({
                        type: 'edit-send-destination'
                      })
                    }
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                  >
                    Edit
                  </Button>
                }
                title={<Typography.Text style={{ ...labelTitleStyle }}>Where do you want to send this Credential?</Typography.Text>}
                style={{ marginTop: '24px' }}
              >
                <Descriptions.Item label="Institution Name" labelStyle={labelStyle}>
                  {props.application?.sendDestination?.entityName ?? 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isSendDestinationEditable && (
              <Form
                layout="vertical"
                form={caseSendDestinationForm}
                initialValues={{
                  sendDestination: props.application?.sendDestination?.entityName
                }}
                onFinish={handleSaveSendDestination}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button onClick={handleCancelEditSendDestination}>Cancel</Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Where do you want to send this Credential?</Typography.Text>}
                    labelStyle={labelStyle}
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item name={['sendDestination']} style={{ marginTop: '24px' }}>
                      <Select style={{ width: 200 }} placeholder="Select an institution" disabled={disabled}>
                        {props.entities
                          .filter((entity) => entity.isClient)
                          .map((entity) => (
                            <Select.Option key={entity.id} value={entity.id} disabled={disabled}>
                              {entity.entityName}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
            <Descriptions
              layout="vertical"
              style={{ marginTop: '40px' }}
              title={<Typography.Text style={{ fontSize: '20px', fontWeight: '600' }}>Private Case Details</Typography.Text>}
            />
            <Typography.Text style={{ fontSize: '12px' }}>(Not Visible to Applicant)</Typography.Text>
            <br />
            <Descriptions layout="vertical" title={<Typography.Text style={{ ...labelTitleStyle }}>Institution Contact Details</Typography.Text>}>
              <Descriptions.Item label="Institution Email" labelStyle={labelStyle}>
                {props.applicationReview?.privateCaseDetails?.institutionContactDetails?.institutionEmail ?? 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Institution Phone" labelStyle={labelStyle}>
                {props.applicationReview?.privateCaseDetails?.institutionContactDetails?.institutionPhone ?? 'N/A'}
              </Descriptions.Item>
            </Descriptions>
            {!applicationFormState.isAlternateVerifyingEntityEditable && (
              <Descriptions
                layout="vertical"
                column={1}
                extra={
                  <Button
                    type="primary"
                    onClick={() => {
                      dispatch({
                        type: 'edit-alternate-verifying-entity'
                      });
                    }}
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                  >
                    Edit
                  </Button>
                }
                title={<Typography.Text style={{ ...labelTitleStyle }}>Alternate Verifying Entity</Typography.Text>}
                style={{ marginTop: '24px' }}
              >
                <Descriptions.Item>{props.applicationReview?.privateCaseDetails?.alternateVerifyingEntity?.isVerifiedWithOther ? 'Yes' : 'No'}</Descriptions.Item>
                <Descriptions.Item style={{ textAlign: 'justify', width: '40%' }} label="Other Details" labelStyle={labelStyle}>
                  {props.applicationReview?.privateCaseDetails?.alternateVerifyingEntity?.otherDetails ?? 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isAlternateVerifyingEntityEditable && (
              <Form
                style={{ marginTop: '24px' }}
                form={caseAlternateVerifyingEntityForm}
                layout="vertical"
                initialValues={{
                  isVerifiedWithOther: props.applicationReview?.privateCaseDetails?.alternateVerifyingEntity?.isVerifiedWithOther,
                  otherDetails: props.applicationReview?.privateCaseDetails?.alternateVerifyingEntity?.otherDetails
                }}
                onFinish={handleSaveAlternateVerifyingEntity}
              >
                <Row>
                  <Col flex="auto">
                    <Form.Item
                      getValueProps={(value) => {
                        return {
                          value: value
                        };
                      }}
                      valuePropName="value"
                      name={['isVerifiedWithOther']}
                      label="Alternate Verifying Entity"
                    >
                      <Radio.Group defaultValue={props.applicationReview?.privateCaseDetails?.alternateVerifyingEntity?.isVerifiedWithOther ?? false}>
                        <Radio value={true} disabled={disabled}>
                          Yes
                        </Radio>
                        <Radio value={false} disabled={disabled}>
                          No
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                    {isVerifiedWithOther && (
                      <Form.Item label="Other Details" name={['otherDetails']}>
                        <TextArea style={{ width: 500 }} placeholder="Please enter description" autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} disabled={disabled} />
                      </Form.Item>
                    )}
                  </Col>
                  <Col flex={'100px'} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleCancelEditAlternateVerifyingEntity}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                        Save
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}

            {!applicationFormState.isVerificationStatusEditable && (
              <Descriptions
                layout="vertical"
                extra={
                  <Button
                    type="primary"
                    disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                    onClick={() =>
                      dispatch({
                        type: 'edit-verification-status'
                      })
                    }
                  >
                    Edit
                  </Button>
                }
                title={<Typography.Text style={{ ...labelTitleStyle }}>Verification Status</Typography.Text>}
                style={{ marginTop: '24px' }}
              >
                <Descriptions.Item label="Verification Response" labelStyle={labelStyle}>
                  {verificationStatus.filter((status) => status.value === props.applicationReview?.privateCaseDetails?.verificationStatus?.verificationResponse)[0]?.label ??
                    'N/A'}
                </Descriptions.Item>
              </Descriptions>
            )}
            {applicationFormState.isVerificationStatusEditable && (
              <Form
                layout="vertical"
                form={caseVerificationStatusForm}
                initialValues={{
                  verificationResponse: props.applicationReview?.privateCaseDetails?.verificationStatus?.verificationResponse
                }}
                onFinish={handleSaveVerificationStatus}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button onClick={handleCancelEditVerificationStatus}>Cancel</Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication} disabled={disabled}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Verification Status</Typography.Text>}
                    labelStyle={labelStyle}
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item name={['verificationResponse']}>
                      <Select style={{ width: 300 }} placeholder={'Verification Response'} disabled={disabled}>
                        {verificationStatus.map((status) => (
                          <Select.Option key={status.key} value={status.value}>
                            {status.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
            <div
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                marginTop: '36px'
              }}
            >
              <Typography.Text style={{ ...labelTitleStyle, paddingTop: '4px', marginBottom: '24px' }}>Entity Supplied Credential</Typography.Text>

              {!applicationFormState.isEntitySuppliedCredentialEditable && (
                <Button
                  type="primary"
                  onClick={() =>
                    dispatch({
                      type: 'edit-entity-supplied-credential'
                    })
                  }
                  disabled={disabled || Object.values(applicationFormState).some((value) => value)}
                >
                  Edit
                </Button>
              )}
              {applicationFormState.isEntitySuppliedCredentialEditable && (
                <Button
                  type="default"
                  onClick={() =>
                    dispatch({
                      type: 'cancel-entity-supplied-credential'
                    })
                  }
                >
                  Cancel
                </Button>
              )}
            </div>
            {!applicationFormState.isEntitySuppliedCredentialEditable && (
              <div style={{ ...descriptionStyle, width: '65%', display: 'flex', justifyContent: 'space-between' }}>
                <CredCaseFileUploadButtonWithPreviewContainer
                  caseId={props.caseId}
                  title={''}
                  buttonText={'Upload Entity Supplied Credential'}
                  permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                  permittedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                  isPreviewOnly={true}
                  isShowFileName={false}
                  fieldName={'credential'}
                  fileName={'entity-supplied-credential'}
                  onUploadSuccess={(response) => {
                    props.handleOnUploadSuccessEntityProvidedCredentials(response, 'credential');
                  }}
                />
                <CredCaseFileUploadButtonWithPreviewContainer
                  caseId={props.caseId}
                  title={''}
                  buttonText={'Upload Entity Supplied Translation'}
                  permittedContentTypes={['application/pdf']}
                  permittedExtensions={['pdf']}
                  isPreviewOnly={true}
                  isShowFileName={false}
                  fieldName={'translation'}
                  fileName={'entity-translation-for-entity-supplied-credential'}
                  onUploadSuccess={(response) => {
                    props.handleOnUploadSuccessEntityProvidedCredentials(response, 'translation');
                  }}
                />
              </div>
            )}
            {applicationFormState.isEntitySuppliedCredentialEditable && (
              <Row>
                <Col style={{ width: 'calc(100% - 100px)', display: 'flex' }}>
                  <CredCaseFileUploadButtonWithPreviewContainer
                    caseId={props.caseId}
                    title={''}
                    buttonText={'Upload Entity Supplied Credential'}
                    permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                    permittedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                    isPreviewOnly={false}
                    isShowFileName={false}
                    fieldName={'credential'}
                    fileName={'entity-supplied-credential'}
                    onUploadSuccess={(response) => {
                      props.handleOnUploadSuccessEntityProvidedCredentials(response, 'credential');
                      applicationFormState.isEntitySuppliedCredentialEditable = false;
                    }}
                  />
                  <CredCaseFileUploadButtonWithPreviewContainer
                    caseId={props.caseId}
                    title={''}
                    buttonText={'Upload Entity Supplied Translation'}
                    permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                    permittedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                    isPreviewOnly={false}
                    isShowFileName={false}
                    fieldName={'translation'}
                    fileName={'entity-translation-for-entity-supplied-credential'}
                    onUploadSuccess={(response) => {
                      props.handleOnUploadSuccessEntityProvidedCredentials(response, 'translation');
                      applicationFormState.isEntitySuppliedCredentialEditable = false;
                    }}
                  />
                </Col>
              </Row>
            )}
          </div>

          {!applicationFormState.isDatesEditable && props?.staffUserType === 'Staff.IS' && (
            <>
              <>
                <br />
                <Descriptions
                  title={<Typography.Text style={{ ...labelTitleStyle }}>Dates Details</Typography.Text>}
                  layout="vertical"
                  labelStyle={labelStyle}
                  style={{ paddingLeft: 30 }}
                  extra={
                    <Button
                      type="primary"
                      disabled={Object.values(applicationFormState).some((value) => value)}
                      onClick={() => {
                        dispatch({
                          type: 'edit-dates'
                        });
                      }}
                    >
                      Edit
                    </Button>
                  }
                >
                  <Descriptions.Item label="Purge Eligible At">{props.purgeEligibleAt ? FormatDate(props.purgeEligibleAt) : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Archive Eligible At">{props.archiveEligibleAt ? FormatDate(props.archiveEligibleAt) : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Expiration Eligible At">{props.expirationEligibleAt ? FormatDate(props.expirationEligibleAt) : 'N/A'}</Descriptions.Item>
                </Descriptions>
              </>
              <div
                style={{
                  width: '100%',
                  paddingLeft: 32,
                  borderRadius: borderRadiusLG,
                  background: colorBgContainer,
                  boxShadow: `0 2px 8px ${colorBgContainer}`
                }}
              ></div>
            </>
          )}
          {applicationFormState.isDatesEditable && props?.staffUserType === 'Staff.IS' && (
            <div
              style={{
                width: '100%',
                paddingLeft: 32,
                borderRadius: borderRadiusLG,
                background: colorBgContainer,
                boxShadow: `0 2px 8px ${colorBgContainer}`
              }}
            >
              <Form
                form={overrideDetailsForm}
                style={{ width: '100%' }}
                layout="vertical"
                initialValues={{
                  purgeEligibleAt: props.purgeEligibleAt ? dayjs(props.purgeEligibleAt) : null,
                  archiveEligibleAt: props.archiveEligibleAt ? dayjs(props.archiveEligibleAt) : null,
                  expirationEligibleAt: props.expirationEligibleAt ? dayjs(props.expirationEligibleAt) : null
                }}
                onFinish={handleSaveOverrideDetails}
              >
                <Row>
                  <Descriptions
                    column={2}
                    layout="vertical"
                    extra={
                      <Col flex={'100px'} style={{ textAlign: 'right' }}>
                        <Space>
                          <Button
                            onClick={() => {
                              dispatch({
                                type: 'cancel-edit-dates'
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="primary" htmlType="submit" loading={isSavingApplication}>
                            Save
                          </Button>
                        </Space>
                      </Col>
                    }
                    title={<Typography.Text style={{ ...labelTitleStyle }}>Dates Details</Typography.Text>}
                    labelStyle={labelStyle}
                    style={{ marginTop: '24px' }}
                  />
                  <Col flex="auto">
                    <Form.Item label="Purge Eligible At" name={['purgeEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={props.purgeEligibleAt}
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                        placeholder="Purge Eligible At"
                        disabled={props.caseState !== 'draft'}
                      />
                    </Form.Item>
                    <Form.Item label="Archive Eligible At" name={['archiveEligibleAt']}>
                      <DatePicker
                        style={{ width: 300, fontSize: '1px' }}
                        value={props.archiveEligibleAt}
                        placeholder="Archive Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                      />
                    </Form.Item>
                    <Form.Item label="Expiration Eligible At" name={['expirationEligibleAt']}>
                      <DatePicker
                        style={{ width: 300 }}
                        value={props.expirationEligibleAt}
                        placeholder="Expiration Eligible At"
                        format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};
